import { Fragment } from 'react'

import { ChevronRight } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { Skeleton } from '~/components/component-library'
import { Button } from '~/components/component-library/Button'
import { cn } from '~/utils/tailwind-utils'

import type {
  BreadcrumbItem as BreadcrumbItemType,
  BreadcrumbProps,
} from '~/components/component-library/Breadcrumb/types'

export type {
  BreadcrumbProps,
  BreadcrumbItem,
} from '~/components/component-library/Breadcrumb/types'

const BreadcrumbItem = ({
  label,
  href,
  isCurrent = false,
}: BreadcrumbItemType & { isCurrent?: boolean }) => {
  const button = (
    <Button
      variant="tertiary"
      size="small"
      className={cn('px-3', isCurrent && 'pointer-events-none cursor-default')}
      aria-current={isCurrent ? 'page' : undefined}
    >
      <span className="max-w-32 truncate">{label}</span>
    </Button>
  )

  if (href && !isCurrent) {
    return <SplitsLink path={href}>{button}</SplitsLink>
  }

  return button
}

const BreadcrumbSeparator = () => (
  <ChevronRight className="h-4 w-4 text-secondary" />
)

const BreadcrumbSkeleton = () => (
  <nav aria-label="Breadcrumb" className="flex items-center">
    <Button
      variant="tertiary"
      size="small"
      className="pointer-events-none px-3"
    >
      <Skeleton size="md" length={4} />
    </Button>
    <BreadcrumbSeparator />
    <Button
      variant="tertiary"
      size="small"
      className="pointer-events-none px-3"
    >
      <Skeleton size="md" length={6} />
    </Button>
  </nav>
)

export const Breadcrumb = ({ items, isLoading }: BreadcrumbProps) => {
  if (isLoading) return <BreadcrumbSkeleton />

  return (
    <nav aria-label="Breadcrumb" className="flex items-center">
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        return (
          <Fragment key={`${item.label}-${index}`}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem {...item} isCurrent={isLast} />
          </Fragment>
        )
      })}
    </nav>
  )
}
